import React, { useEffect, useRef } from 'react';
import './InfoModal.css';

/**
 * ============================================================================
 * [i] INFO WINDOW COMPONENT (InfoModal.tsx)
 * ============================================================================
 * This component is triggered when the visitor clicks the 'i' letter scrap.
 * It renders a museum archival legal & technical dossier modal over the exhibition.
 *
 * YOU CAN FREELY CUSTOMIZE THIS COMPONENT:
 * - Edit, add, or remove <section className="info-section"> blocks below
 * - Change the headers, artist statements, bio, provenance, or copyright notices
 * - Modify dialog styles in InfoModal.css
 * ============================================================================
 */

export interface InfoModalProps {
  /** Whether the info modal window is visible */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
}

export const InfoModal: React.FC<InfoModalProps> = ({ isOpen, onClose }) => {
  const dialogRef = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (isOpen) {
      if (!dialog.open) {
        dialog.showModal();
      }
    } else {
      if (dialog.open) {
        dialog.close();
      }
    }
  }, [isOpen]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const handleClose = () => {
      onClose();
    };

    dialog.addEventListener('close', handleClose);

    // Close when clicking outside modal content (the dark backdrop)
    const handleBackdropClick = (event: MouseEvent) => {
      if (event.target !== dialog) return;

      const rect = dialog.getBoundingClientRect();
      const isInside =
        rect.top <= event.clientY &&
        event.clientY <= rect.top + rect.height &&
        rect.left <= event.clientX &&
        event.clientX <= rect.left + rect.width;

      if (!isInside) {
        dialog.close();
      }
    };

    dialog.addEventListener('click', handleBackdropClick);

    return () => {
      dialog.removeEventListener('close', handleClose);
      dialog.removeEventListener('click', handleBackdropClick);
    };
  }, [onClose]);

  return (
    <dialog
      ref={dialogRef}
      id="schz-info-dialog"
      className="schz-info-dialog"
      aria-labelledby="info-dialog-title"
    >
      <div className="info-modal-card">
        <header className="info-modal-header">
          <h2 id="info-dialog-title">INFO & LEGAL NOTICE</h2>
        </header>

        {/* ================================================================= */}
        {/* MODAL BODY CONTENT                                                */}
        {/* ================================================================= */}
        <section className="info-modal-content">
          {/* SECTION 1: ARTIST & PROVENANCE */}
          <div className="info-section">
            <h3>ARTIST &amp; PROVENANCE</h3>
            <div className="info-row">
              <strong className="info-label">Artist:</strong>
              <span className="info-value">schzstrg (b. 2003)</span>
            </div>
            <div className="info-row">
              <strong className="info-label">Archive Established:</strong>
              <span className="info-value">2026</span>
            </div>
          </div>

          {/* SECTION 2: EXHIBIT / WORK */}
          <div className="info-section">
            <h3>EXHIBIT / WORK</h3>
            <div className="info-row">
              <strong className="info-label">Rightsholder:</strong>
              <span className="info-value">All artworks displayed herein are original works of schzstrg.</span>
            </div>
            <div className="info-row">
              <strong className="info-label">Timespan:</strong>
              <span className="info-value">All artworks have been produced from 2018 to <span id="currentYear">{new Date().getFullYear()}</span> and serve an archival purpose.</span>
            </div>
          </div>

          {/* SECTION 3: TERMS OF USE */}
          <div className="info-section">
            <h3>TERMS OF USE</h3>
            <div className="info-terms-list">
              <div className="info-row">
                <strong className="info-label">Personal Display Only:</strong>
                <span className="info-value">
                  Viewing is permitted for personal, non-commercial display. Unauthorized re-hosting, redistribution, modification, sampling, or commercial exploitation is prohibited.
                </span>
              </div>
              <div className="info-row">
                <strong className="info-label">AI Scraping Reservation:</strong>
                <span className="info-value">
                  In accordance with Art. 4(3) Directive (EU) 2019/790 and § 44b UrhG, all rights are expressly reserved regarding text and data mining, machine learning, and AI model training.
                </span>
              </div>
              <div className="info-row">
                <strong className="info-label">Warranty:</strong>
                <span className="info-value">
                  Provided &quot;as is&quot; with no warranty regarding client performance or rendering stability.
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* ================================================================= */}
        {/* MODAL FOOTER: Dismiss button                                      */}
        {/* ================================================================= */}
        <footer className="info-modal-footer">
          <form method="dialog">
            <button type="submit" className="info-close-btn" autoFocus>
              DISMISS
            </button>
          </form>
        </footer>
      </div>
    </dialog>
  );
};

export default InfoModal;
